import { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import { EditorToolbar, formats } from "./EditorToolbar";
import "react-quill/dist/quill.snow.css";
import type { DefaultTheme } from "styled-components";
import styled from "styled-components";
import type { RegisterOptions, useForm } from "react-hook-form";
import { showErrors } from "../../util/util-components";
import { useTranslation } from "react-i18next";
import { getVisibleTextLengthFromRichEditor } from "../../pages/admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/AssetsUtil";

interface IEditorProps {
  invalid: boolean;
  warn?: boolean;
  theme: DefaultTheme;
  isFocused?: boolean;
  readOnly?: boolean;
  disableList?: boolean;
  disableIndent?: boolean;
}

const EditorWrapper = styled.div<IEditorProps>`
  position: relative;
  overflow: ${({ invalid }) => (invalid ? "visible" : "hidden")};
  background: ${({ theme, readOnly }) =>
    readOnly ? theme.disabledInput : theme.primaryBG};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  margin-bottom: 18px;
  border: ${({ invalid, warn, theme, isFocused }) =>
    (invalid && `${theme.resolve("border_2xl")} solid ${theme.errorColor}`) ||
    (warn && `${theme.resolve("border_md")} solid ${theme.warningTextColor}`) ||
    (isFocused &&
      !invalid &&
      `${theme.resolve("border_md")} solid ${theme.tertiaryBorder}`) ||
    `${theme.resolve("border_md")} solid ${theme.primaryBorder}`};
  box-shadow: ${({ invalid, warn, theme, isFocused }) =>
    (invalid && `0`) ||
    (warn && `0px 0px 1px 1px ${theme.warningTextColor}`) ||
    (isFocused && !invalid && `0px 0px 1px 1px ${theme.tertiaryBorder}`) ||
    `0`};
  .quill {
    max-height: 100%;
  }
  .ql-container {
    border: 0;
    max-height: 100%;
  }

  .ql-editor {
    padding: ${({ readOnly }) => (readOnly ? "0 13px 13px" : `13px`)};
    min-height: 120px;
    /* max-height: 400px; */ // AD-4809 - Removed max-height.
  }

  .ql-editing {
    left: 8px !important;
  }

  .ql-toolbar {
    display: ${({ readOnly }) => (readOnly ? "none" : "block")};
    border: ${({ theme }) =>
      `${theme.resolve("border_md")} solid ${theme.primaryBorder}`};
    border-right: 0;
    border-left: 0;
    padding: 5px 0;
    margin: 0 13px;
    button {
      border: ${({ theme }) =>
        `${theme.resolve("border_md")} solid ${theme.primaryBorder}`};
      padding: 5px;
      width: 30px;
      height: 30px;
      margin-right: 5px;
      border-radius: ${({ theme }) => theme.resolve("radius_xs")};
      svg {
        float: none;
        text-align: center;
        fill: ${({ theme }) => theme.secondaryTextColor};
        height: 18px;
      }
      &.ql-active {
        border: ${({ theme }) =>
          `${theme.resolve("border_md")} solid ${theme.selectedBorder}`};
        background: ${({ theme }) => theme.secondaryButtonBG};
        svg {
          fill: ${({ theme }) => theme.secondaryButtonTextColor};
        }
      }
    }
  }

  .errorMessageWrapper {
    top: auto !important;
    bottom: -15px;
  }
`;

const FloatingLabel = styled.div<IEditorProps>`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme, invalid }) =>
    invalid ? theme.errorColor : theme.secondaryTextColor};
  pointer-events: none;
  padding: 13px 13px ${({ theme }) => theme.resolve("spacing_md")};
`;

//Rich Text Editor component
export const RichEditor = ({
  value,
  name,
  label,
  placeholder,
  readOnly,
  onChange,
  useFormMethods: { errors, register, setValue, watch, clearErrors },
  disableList,
  disableIndent,
  id,
  validationRules,
}: {
  value: string | undefined;
  name: string;
  label: string;
  placeholder?: string;
  readOnly?: boolean;
  disableList?: boolean;
  disableIndent?: boolean;
  id?: string;
  onChange?: (value: string) => void;
  useFormMethods: Pick<
    ReturnType<typeof useForm>,
    "register" | "errors" | "formState" | "setValue" | "watch"
  > & { clearErrors?: ReturnType<typeof useForm>["clearErrors"] };
  validationRules?: RegisterOptions;
}) => {
  const [state, setState] = useState(value);
  const [invalid, setInvalid] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const { t } = useTranslation();

  const modules = {
    toolbar: readOnly
      ? false
      : {
          container: `#rich-${id ?? name}-editor-toolbar`,
        },
    clipboard: {
      matchVisual: false,
    },
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: true,
    },
  };

  const updateValue = (value: string) => {
    setState(value);
    if (name) {
      setValue(name, value === "<p><br></p>" ? "" : value);
    }
    if (onChange) {
      onChange(value);
    }
  };

  // Watch for external changes to the form value
  const currentValue = watch(name);

  useEffect(() => {
    if (!isRegistered) {
      register(name, validationRules || {});
      setIsRegistered(true);
    }
  }, [isRegistered, name, register, validationRules]);

  useEffect(() => {
    // Sync internal state when value prop changes (e.g., from external default value calls)
    if (value === undefined || value === null) return;
    const updated_value = value === "" ? "<p><br></p>" : value;
    setState((prev) => (updated_value !== prev ? updated_value : prev));
    if (name) {
      setValue(name, value);
    }
  }, [name, setValue, value]);

  useEffect(() => {
    setInvalid(errors[name]?.message || errors[name]?.type);
  }, [errors, name]);

  useEffect(() => {
    const plainLength = getVisibleTextLengthFromRichEditor(currentValue || "");
    if (plainLength > 0) {
      setInvalid(false);
      clearErrors?.(name);
    }
  }, [name, currentValue, clearErrors]);

  return (
    <>
      <EditorWrapper
        invalid={invalid}
        isFocused={isFocused}
        readOnly={readOnly}
      >
        <FloatingLabel invalid={invalid}>{label}</FloatingLabel>
        {!readOnly && (
          <EditorToolbar
            name={name}
            id={id}
            readOnly={readOnly}
            disableList={disableList}
            disableIndent={disableIndent}
          />
        )}
        <ReactQuill
          theme="snow"
          value={state || ""}
          onChange={updateValue}
          placeholder={placeholder}
          modules={modules}
          formats={formats}
          readOnly={readOnly}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          bounds={".quill"}
        />
        {showErrors({ errors, name, t })}
      </EditorWrapper>
    </>
  );
};
